/**
 * MCM Algolia core — shared by the header search drop-down (snippets/algolia-autocomplete.liquid)
 * and the search results page (sections/algolia-search-results.liquid).
 *
 * Search / visibility / variant matching follow 28-09-algolia-switch (algolia-autocomplete + algolia-plp).
 * Homepage collection rails (sections/mcm-collection-rail.liquid) use browseCollection().
 * Pricing is computed by mcm-algolia-snap-parity.js (enrichHit) using product .js + the
 * mcm-algolia-pricing-bridge section, then displayed as "price / Members: price".
 *
 * Config comes from window.McmAlgoliaConfig (snippets/algolia-config.liquid).
 */
(function (global) {
  'use strict';

  var cfg = global.McmAlgoliaConfig;
  if (!cfg) return;

  var client = null;
  var readyCallbacks = [];
  var productCache = {};
  var bridgeCache = {};
  var searchCache = {};
  var BRIDGE_SECTION = 'mcm-algolia-pricing-bridge';

  var RETRIEVE = [
    'title',
    'name',
    'handle',
    'url',
    'image',
    'product_image',
    'price',
    'compare_at_price',
    'variants_min_price',
    'variants_max_price',
    'variants_compare_at_price_min',
    'member_price',
    'member_sale_price',
    'member_pricing',
    'product_id',
    'id',
    'objectID',
    'meta',
    'named_tags',
    'tags',
    '_tags',
    'position',
    'variant_title',
    'option1',
    'option2',
    'option3',
    'options',
    'variant_id',
    'product_type',
    'published_at',
    'created_at',
    'ss_tags',
    'ss_available',
  ];

  var COLOUR_WORDS = {
    blue: 'Blue', grey: 'Grey', gray: 'Grey', white: 'White', black: 'Black', green: 'Green',
    brown: 'Brown', natural: 'Natural', orange: 'Orange', yellow: 'Yellow', red: 'Red', pink: 'Pink',
    navy: 'Navy', beige: 'Beige', ivory: 'Ivory', cream: 'Cream', charcoal: 'Charcoal', gold: 'Gold',
    silver: 'Silver', purple: 'Purple', olive: 'Olive', teal: 'Teal', sand: 'Sand', blush: 'Blush',
    rust: 'Rust', taupe: 'Taupe', tan: 'Tan',
  };
  var CATEGORY_WORDS = { sofa: 'Sofas', sofas: 'Sofas' };

  /* ---------- client ---------- */

  function ready(cb) {
    if (client) {
      cb(client);
      return;
    }
    readyCallbacks.push(cb);
    if (readyCallbacks.length > 1) return;
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (global.algoliasearch) {
        clearInterval(timer);
        client = global.algoliasearch(cfg.appId, cfg.searchKey);
        var queue = readyCallbacks.slice();
        readyCallbacks = [];
        queue.forEach(function (fn) {
          fn(client);
        });
      } else if (tries > 150) {
        clearInterval(timer);
        console.warn('[MCM Algolia] search client failed to load');
      }
    }, 100);
  }

  /* ---------- formatting ---------- */

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function handleize(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function toCents(value) {
    if (value == null || value === '') return null;
    var amount = Number(value);
    if (isNaN(amount)) return null;
    if (amount >= 100000) return Math.round(amount);
    return Math.round(amount * 100);
  }

  function groupDigits(intStr, sep) {
    return String(intStr).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  function formatMoney(value) {
    var cents = toCents(value);
    if (cents == null) return '';
    var format = cfg.moneyFormat || '${{amount}}';
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = match ? match[1] : 'amount';
    var dollars = (cents / 100).toFixed(2).split('.');
    var out;
    if (key === 'amount_no_decimals') out = groupDigits(Math.round(cents / 100), ',');
    else if (key === 'amount_with_comma_separator') out = groupDigits(dollars[0], '.') + ',' + dollars[1];
    else if (key === 'amount_no_decimals_with_comma_separator') out = groupDigits(Math.round(cents / 100), '.');
    else if (key === 'amount_with_apostrophe_separator') out = groupDigits(dollars[0], "'") + '.' + dollars[1];
    else out = groupDigits(dollars[0], ',') + '.' + dollars[1];
    return match ? format.replace(match[0], out).replace(/<[^>]*>/g, '') : '$' + out;
  }

  /* ---------- hit helpers ---------- */

  function productTitle(hit) {
    return hit.title || hit.name || hit.product_title || 'Product';
  }

  function productHandle(hit) {
    if (hit.handle) return String(hit.handle);
    var m = String(hit.url || '').match(/\/products\/([^/?#]+)/i);
    return m ? m[1] : '';
  }

  function tagList(hit) {
    return []
      .concat((hit && hit.tags) || [])
      .concat((hit && hit._tags) || [])
      .map(function (t) {
        return String(t || '').trim().toLowerCase();
      });
  }

  function matchedVariantIdFromHit(hit) {
    if (!hit) return null;
    if (hit._matchedVariantId) return String(hit._matchedVariantId);
    if (hit.variant_id != null && /^\d+$/.test(String(hit.variant_id))) return String(hit.variant_id);
    if (hit.objectID != null && /^\d+$/.test(String(hit.objectID))) return String(hit.objectID);
    return null;
  }

  function pinMatchedVariant(hit) {
    var vid = matchedVariantIdFromHit(hit);
    if (vid) hit._matchedVariantId = vid;
    return hit;
  }

  // CC 21 Oct 2026 - search: resolve the Algolia-matched variant from Shopify .js
  // (id first, then option values / variant title) so the card can use that variant's image and Color / Size.
  function resolveShopifyVariant(hit, product) {
    if (!hit || !product || !Array.isArray(product.variants) || !product.variants.length) return null;
    var variants = product.variants;
    var vid = matchedVariantIdFromHit(hit);
    var i;
    if (vid) {
      for (i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(vid)) return variants[i];
      }
    }
    var o1 = String(hit.option1 || '').trim();
    var o2 = String(hit.option2 || '').trim();
    var o3 = String(hit.option3 || '').trim();
    if (o1 || o2 || o3) {
      for (i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (
          (!o1 || String(v.option1 || '').trim() === o1) &&
          (!o2 || String(v.option2 || '').trim() === o2) &&
          (!o3 || String(v.option3 || '').trim() === o3)
        ) {
          return v;
        }
      }
    }
    var title = String(hit.variant_title || '').trim();
    if (title && !/^default(\s+title)?$/i.test(title)) {
      for (i = 0; i < variants.length; i++) {
        if (String(variants[i].title || '').trim() === title) return variants[i];
      }
    }
    return null;
  }

  function productUrl(hit) {
    var base = hit.url || (productHandle(hit) ? '/products/' + productHandle(hit) : cfg.searchUrl);
    base = String(base).split('?')[0];

    // CG 2026-10-28 - Only use the base, as variants don't seem to be relevant to client
    return base;

    var vid =
      hit._matchedVariantId ||
      (hit._snap && hit._snap.displayVariantId) ||
      hit._plpDisplayVariantId ||
      null;
    if (vid) base += '?variant=' + encodeURIComponent(vid);
    return base;
  }

  function imageSrc(img) {
    if (!img) return '';
    if (typeof img === 'string') return img;
    if (img.preview_image) return imageSrc(img.preview_image);
    return img.src || img.url || img.original_src || '';
  }

  function productImages(hit, opts) {
    opts = opts || {};
    var product = hit._shopify;
    var gallery = [];
    if (product && Array.isArray(product.images)) {
      product.images.forEach(function (img) {
        var u = imageSrc(img);
        if (u && gallery.indexOf(u) === -1) gallery.push(u);
      });
    }
    /* CC 26 Oct 2026 - L14 (confirmed by CP): products are being re-ordered so image 1 is the deep etch.
       Collection pages + recommendation rails: lead with image 1, hover to image 2.
       Search: lead with the matched colour's own image (as before), hover to image 1/2.
       Image 1 is already in the Algolia record (product_image), so the first paint shows the final
       image - no swap when Shopify data arrives. We keep Algolia's URL when it's the same file so the
       image isn't re-requested, and remember it because enrichment later overwrites product_image. */
    var fileOf = function (u) { return String(u || '').split('?')[0].split('/').pop(); };
    if (!product && hit._plpFirstImage === undefined) hit._plpFirstImage = hit.product_image || hit.image || '';
    var first = gallery[0] || hit._plpFirstImage || hit.product_image || hit.image || '';
    if (hit._plpFirstImage && fileOf(first) === fileOf(hit._plpFirstImage)) first = hit._plpFirstImage;
    var onCollection = /^\/collections\//.test(String((global.location && global.location.pathname) || ''));
    var primary = (opts.deepEtch || onCollection) ? first : (hit._matchedImage || hit.image || first);
    var hover = '';
    [gallery[1], gallery[0]].forEach(function (g) { if (!hover && g && fileOf(g) !== fileOf(primary)) hover = g; });
    return { primary: primary, hover: hover };
  }

  function sizedImage(src, width) {
    if (!src) return '';
    var url = String(src);
    if (url.indexOf('//') === 0) url = 'https:' + url;
    if (url.indexOf('cdn.shopify.com') === -1 && url.indexOf('/cdn/shop/') === -1) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function categoriesOf(hit) {
    var out = [];
    []
      .concat(hit.tags || [])
      .concat(hit._tags || [])
      .forEach(function (tag) {
        var m = String(tag || '').match(/^category:\s*(.+)$/i);
        if (m && out.indexOf(m[1].trim()) === -1) out.push(m[1].trim());
      });
    return out;
  }

  /* ---------- query parsing / matching (28-09 algolia-plp) ---------- */

  function parseQuery(query) {
    var words = String(query || '').toLowerCase().match(/[a-z0-9]+/g) || [];
    var colours = [];
    var categories = [];
    var nameWords = [];
    words.forEach(function (word) {
      /* Track known colour words for variant ranking only — keep them in the
         query text so free-text search is not forced through Colour: tags. */
      if (COLOUR_WORDS[word] && colours.indexOf(COLOUR_WORDS[word]) === -1) {
        colours.push(COLOUR_WORDS[word]);
      }
      if (CATEGORY_WORDS[word] && categories.indexOf(CATEGORY_WORDS[word]) === -1) {
        categories.push(CATEGORY_WORDS[word]);
      }
      nameWords.push(word);
    });
    return { words: words, colours: colours, categories: categories, nameWords: nameWords };
  }

  function titleHasWord(title, word) {
    var escaped = String(word || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp('(?:^|[^a-z0-9])' + escaped + 's?(?:[^a-z0-9]|$)', 'i').test(String(title || ''));
  }

  function titleHasPrefix(title, word) {
    var escaped = String(word || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp('(?:^|[^a-z0-9])' + escaped, 'i').test(String(title || ''));
  }

  function variantBlob(hit) {
    return [hit.variant_title, hit.option1, hit.option2, hit.option3].join(' ').toLowerCase();
  }

  function hitHasColour(hit, label) {
    var want = String(label || '').toLowerCase();
    if (tagList(hit).indexOf('colour:' + want) !== -1) return true;
    return variantBlob(hit).indexOf(want) !== -1;
  }

  /**
   * Ranking: products whose title holds every word come first.
   * Last word may be partial while typing ("dini" → dining); earlier words must be whole words (plural allowed).
   */
  function titleMatchesAll(hit, parts, allowPrefixLast) {
    var title = productTitle(hit);
    var tags = tagList(hit);
    for (var j = 0; j < parts.nameWords.length; j++) {
      var word = parts.nameWords[j];
      var isLast = j === parts.nameWords.length - 1;
      var category = CATEGORY_WORDS[word];
      var inTitle = titleHasWord(title, word) || (allowPrefixLast && isLast && titleHasPrefix(title, word));
      var inCategory = category && tags.indexOf('category:' + category.toLowerCase()) !== -1;
      if (!inTitle && !inCategory) return false;
    }
    return true;
  }

  function failsVisibility(hit) {
    var tags = tagList(hit);
    if (tags.indexOf('ss_hide') !== -1) return true;
    if (cfg.site && cfg.site.loggedInMember && tags.indexOf('ss_loyalty') !== -1) return true;
    if (tags.indexOf('ss_collection_only') !== -1) return true;
    return false;
  }

  function colourScore(hit, colours) {
    var blob = variantBlob(hit);
    var score = 0;
    (colours || []).forEach(function (colour) {
      if (blob.indexOf(String(colour).toLowerCase()) !== -1) score += 5;
    });
    return score;
  }

  function dedupeMatched(hits, parts, allowPrefixLast) {
    var best = {};
    var order = [];
    (hits || []).forEach(function (hit) {
      /* Colour words stay in the Algolia query; they only boost variant ranking via colourScore.
         Do not hard-filter on Colour: tags — titles like "Tan Bellino" are often tagged Brown/Natural. */
      if (!hit || failsVisibility(hit)) return;
      pinMatchedVariant(hit);
      var key =
        hit.id != null
          ? 'id:' + String(hit.id)
          : productHandle(hit)
            ? 'h:' + productHandle(hit)
            : 'o:' + String(hit.objectID || '');
      var score = colourScore(hit, parts.colours);
      if (!best[key]) {
        best[key] = { hit: hit, score: score };
        order.push(key);
        return;
      }
      if (score > best[key].score) best[key] = { hit: hit, score: score };
    });
    var titleHits = [];
    var otherHits = [];
    order.forEach(function (key) {
      var hit = best[key].hit;
      (titleMatchesAll(hit, parts, allowPrefixLast) ? titleHits : otherHits).push(hit);
    });
    return titleHits.concat(otherHits);
  }

  /**
   * Keyword product search.
   * opts.indexName  — replica for sorting (falls back to primary on error)
   * opts.hitsPerPage — raw variant hits to pull before de-duping by product (default 1000)
   * opts.prefixLast — allow partial last word (live typing)
   * Resolves { hits, nbHits, queryID } with unique, visible, matched products in Algolia rank order.
   */
  function searchProducts(query, opts) {
    opts = opts || {};
    var indexName = opts.indexName || cfg.indexProducts;
    var hitsPerPage = opts.hitsPerPage || 1000;
    var cacheKey = [indexName, hitsPerPage, opts.prefixLast ? 1 : 0, String(query || '').trim().toLowerCase()].join('|');
    if (searchCache[cacheKey]) return searchCache[cacheKey];

    var promise = new Promise(function (resolve, reject) {
      ready(function (c) {
        var parts = parseQuery(query);
        var base = {
          hitsPerPage: hitsPerPage,
          page: 0,
          clickAnalytics: true,
          removeWordsIfNoResults: 'none',
          attributesToRetrieve: RETRIEVE,
        };
        function withFilters(extra) {
          var p = {};
          Object.keys(base).forEach(function (k) {
            p[k] = base[k];
          });
          var list = extra || [];
          if (list.length) p.filters = list.join(' AND ');
          return p;
        }
        var index = c.initIndex(indexName);
        /* Keep colour words in the query (no Colour: tag filter on free-text search).
           Colour list is still used later to prefer matching variants when ranking. */
        var text = parts.nameWords.join(' ');
        /* Whole words anywhere (title / type / tags / description) + a partial last word in the title only,
           so live typing ("dini") doesn't prefix-match unrelated descriptions. */
        var wholeWords = withFilters([]);
        wholeWords.queryType = 'prefixNone';
        wholeWords.ignorePlurals = true;
        var titlePrefix = withFilters([]);
        titlePrefix.restrictSearchableAttributes = ['title', 'handle'];
        var jobs = [index.search(text, wholeWords), index.search(text, titlePrefix)];
        if (parts.categories.length) {
          var nameOnly = parts.nameWords.filter(function (w) {
            return !CATEGORY_WORDS[w];
          });
          var categoryFilters = parts.categories.map(function (category) {
            return 'tags:"Category:' + category + '"';
          });
          jobs.push(index.search(nameOnly.join(' '), withFilters(categoryFilters)));
        }
        Promise.all(jobs)
          .then(function (responses) {
            var raw = [];
            responses.forEach(function (res) {
              raw = raw.concat((res && res.hits) || []);
            });
            var hits = dedupeMatched(raw, parts, !!opts.prefixLast);
            var queryID = responses[0] && responses[0].queryID;
            hits.forEach(function (hit, i) {
              if (queryID) hit.__aaQueryID = queryID;
              hit.__aaPosition = i + 1;
            });
            resolve({ hits: hits, nbHits: hits.length, queryID: queryID });
          })
          .catch(function (err) {
            if (indexName !== cfg.indexProducts) {
              console.warn('[MCM Algolia] index unavailable, using primary:', indexName, err);
              var fallbackOpts = {};
              Object.keys(opts).forEach(function (k) {
                fallbackOpts[k] = opts[k];
              });
              fallbackOpts.indexName = cfg.indexProducts;
              searchProducts(query, fallbackOpts).then(resolve, reject);
              return;
            }
            reject(err);
          });
      });
    });
    searchCache[cacheKey] = promise;
    promise.catch(function () {
      delete searchCache[cacheKey];
    });
    return promise;
  }

  function collectionUrl(hit) {
    if (hit.url) return hit.url;
    if (hit.handle) return '/collections/' + hit.handle;
    return cfg.searchUrl + '?q=' + encodeURIComponent(hit.title || hit.name || '');
  }

  function pagesIndexName() {
    if (cfg.indexPages) return cfg.indexPages;
    var products = cfg.indexProducts || '';
    if (products.slice(-9) === '_products') return products.slice(0, -9) + '_pages';
    return 'shopify_pages';
  }

  var JOURNAL_FILTER = '(meta.custom.is_journal:true OR meta.custom.is_journal:"true")';

  function searchJournals(query, limit) {
    limit = limit || 100;
    return new Promise(function (resolve) {
      ready(function (c) {
        c.initIndex(pagesIndexName())
          .search(String(query || '').trim(), {
            hitsPerPage: limit,
            filters: JOURNAL_FILTER,
            attributesToRetrieve: ['title', 'handle', 'meta'],
            attributesToSnippet: ['body_html:30'],
          })
          .then(function (res) {
            resolve({ hits: (res && res.hits) || [], nbHits: (res && res.nbHits) || 0 });
          })
          .catch(function (err) {
            console.warn('[MCM Algolia] journal search failed', err);
            resolve({ hits: [], nbHits: 0 });
          });
      });
    });
  }

  function plainMeta(hit, key) {
    var meta = hit && hit.meta && hit.meta.custom;
    if (!meta || meta[key] == null || meta[key] === '') return '';
    var value = meta[key];
    if (Array.isArray(value)) value = value[0];
    if (value && typeof value === 'object') {
      if (value.url) return String(value.url);
      if (value.src) return String(value.src);
      if (value.value != null && typeof value.value !== 'object') return String(value.value);
      return '';
    }
    return String(value);
  }

  function journalImageLookup() {
    if (journalImageLookup.cache) return journalImageLookup.cache;
    journalImageLookup.cache = {};
    var el = document.querySelector('[data-mcm-journal-images]');
    if (!el) return journalImageLookup.cache;
    try {
      journalImageLookup.cache = JSON.parse(el.textContent) || {};
    } catch (err) {
      journalImageLookup.cache = {};
    }
    return journalImageLookup.cache;
  }

  function normalizeJournalImage(url) {
    var value = String(url || '').trim();
    if (!value) return '';
    var path = value.match(/\/cdn\/shop\/\S+/);
    if (path) return path[0];
    if (/^https?:\/\//i.test(value)) return value;
    if (value.indexOf('//') === 0) return 'https:' + value;
    return '';
  }

  function journalImageUrl(hit) {
    var raw = plainMeta(hit, 'featured_image').trim();
    var direct = raw.indexOf('gid://') === 0 ? '' : normalizeJournalImage(raw);
    if (direct) return direct;
    var mapped = hit && hit.handle ? journalImageLookup()[hit.handle] : '';
    return normalizeJournalImage(mapped);
  }

  function journalPlainText(value) {
    return String(value || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/\s+/g, ' ')
      .trim();
  }

  function journalStoryTitle(hit) {
    var custom = journalPlainText(plainMeta(hit, 'journal_title'));
    if (custom) return custom;
    return journalPlainText(String((hit && hit.title) || '').replace(/\[hidden\]/gi, ''));
  }

  function journalStoryText(hit) {
    var custom = journalPlainText(plainMeta(hit, 'short_description'));
    if (custom) return custom;
    var snippet = hit && hit._snippetResult && hit._snippetResult.body_html && hit._snippetResult.body_html.value;
    var text = journalPlainText(snippet);
    if (text.length > 180) text = text.slice(0, 177).replace(/\s+\S*$/, '') + '\u2026';
    return text;
  }

  function journalCardsHtml(hits) {
    return (hits || [])
      .map(function (hit) {
        var title = journalStoryTitle(hit);
        if (!title) return '';
        var href = hit.handle ? '/pages/' + hit.handle : '/pages/mcm-house-journal';
        var category = journalPlainText(plainMeta(hit, 'category'));
        var text = journalStoryText(hit);
        var image = journalImageUrl(hit);
        var safeHref = escapeHtml(href);
        return (
          '<article class="mcm-journal-card">' +
          '<a class="mcm-journal-card__media" href="' +
          safeHref +
          '">' +
          (image
            ? '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(title) + '" loading="lazy">'
            : '<span class="mcm-journal-card__placeholder"></span>') +
          '</a>' +
          (category ? '<p class="mcm-journal-card__category">' + escapeHtml(category) + '</p>' : '') +
          '<h3 class="mcm-journal-card__title"><a href="' +
          safeHref +
          '">' +
          escapeHtml(title) +
          '</a></h3>' +
          (text ? '<p class="mcm-journal-card__text">' + escapeHtml(text) + '</p>' : '') +
          '<a class="mcm-journal-card__more" href="' +
          safeHref +
          '">Read more</a>' +
          '</article>'
        );
      })
      .join('');
  }

  function searchCollections(query, limit) {
    limit = limit || 8;
    return new Promise(function (resolve) {
      ready(function (c) {
        if (cfg.indexCollections) {
          c.initIndex(cfg.indexCollections)
            .search(query, { hitsPerPage: limit })
            .then(function (res) {
              resolve(res.hits || []);
            })
            .catch(function () {
              resolve([]);
            });
          return;
        }
        c.initIndex(cfg.indexProducts)
          .searchForFacetValues('collections', query, { maxFacetHits: limit })
          .then(function (res) {
            resolve(
              (res.facetHits || []).map(function (f) {
                return { title: f.value, url: '/collections/' + handleize(f.value) };
              })
            );
          })
          .catch(function () {
            resolve([]);
          });
      });
    });
  }

  function fetchSuggestions(query, limit) {
    return new Promise(function (resolve) {
      if (!cfg.indexSuggestions) {
        resolve(null);
        return;
      }
      ready(function (c) {
        c.initIndex(cfg.indexSuggestions)
          .search(query, { hitsPerPage: limit || 8 })
          .then(function (res) {
            var hits = res.hits || [];
            resolve(
              hits.length
                ? hits.map(function (h) {
                    return String(h.query || h.title || h.name || '');
                  })
                : null
            );
          })
          .catch(function () {
            resolve(null);
          });
      });
    });
  }

  /* ---------- Shopify enrichment + pricing (28-09 enrichSearchHits) ---------- */

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });
    return productCache[handle];
  }

  function parseBridge(html) {
    var m = String(html || '').match(/<script[^>]*data-mcm-algolia-pricing-bridge[^>]*>([\s\S]*?)<\/script>/i);
    if (!m) return null;
    try {
      return JSON.parse(m[1].trim());
    } catch (e) {
      return null;
    }
  }

  function fetchBridge(handle) {
    if (!handle) return Promise.resolve(null);
    if (bridgeCache[handle]) return bridgeCache[handle];
    bridgeCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '?sections=' + BRIDGE_SECTION)
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        return data && data[BRIDGE_SECTION] ? parseBridge(data[BRIDGE_SECTION]) : null;
      })
      .catch(function () {
        return null;
      });
    return bridgeCache[handle];
  }

  function applyMatchedVariantDisplay(hit, query) {
    pinMatchedVariant(hit);
    var product = hit._shopify;
    if (!product || !product.variants || !product.variants.length) return;
    var variant = resolveShopifyVariant(hit, product);
    if (!variant) return;
    hit._matchedVariantId = String(variant.id);
    hit.option1 = variant.option1;
    hit.option2 = variant.option2;
    hit.option3 = variant.option3;
    if (variant.title) hit.variant_title = variant.title;
    var vid = hit._matchedVariantId;
    var parity = global.McmAlgoliaSnapParity;
    /* Price follows the matched variant only when the query names its colour / size;
       the image always follows the variant when that variant has an assigned image. */
    var imageOnly = !!(
      vid &&
      parity &&
      typeof parity.queryNamesVariant === 'function' &&
      !parity.queryNamesVariant(hit, query || '', vid)
    );
    hit._matchedVariantAvailable = variant.available !== false;
    var img = variant.featured_image ? imageSrc(variant.featured_image) : '';
    if (!img && variant.image_id && product.images) {
      for (var j = 0; j < product.images.length; j++) {
        if (String(product.images[j].id) === String(variant.image_id)) {
          img = imageSrc(product.images[j]);
          break;
        }
      }
    }
    if (img) {
      hit._matchedImage = img;
      hit.image = img;
      hit.product_image = img;
    }
    if (imageOnly) return;
    if (variant.price != null) {
      hit.price = variant.price / 100;
      hit.variants_min_price = hit.price;
    }
    if (variant.compare_at_price) hit.compare_at_price = variant.compare_at_price / 100;
  }

  function optionLabel(opt) {
    if (!opt) return '';
    if (typeof opt === 'string') return opt;
    return opt.name || '';
  }

  function productCents(product) {
    if (!product) return 0;
    var cents = product.price_min != null ? Number(product.price_min) : Number(product.price);
    if (cents > 0) return cents;
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var amount = Number(variants[i].price);
      if (amount > 0) return amount;
    }
    return 0;
  }

  function shopifyImageList(product) {
    var out = [];
    if (!product) return out;
    var featured = imageSrc(product.featured_image);
    if (featured) out.push(featured);
    if (Array.isArray(product.images)) {
      product.images.forEach(function (img) {
        var src = imageSrc(img);
        if (src && out.indexOf(src) === -1) out.push(src);
      });
    }
    return out;
  }

  function optionLooksLikeBundle(label) {
    var match = String(label || '').match(/^(.+?)\s+\(([^)]+)\)\s*$/);
    if (!match) return false;
    var kind = match[2].toLowerCase().trim();
    return ['colour', 'color', 'material', 'size', 'quantity', 'qty', 'quantities'].indexOf(kind) !== -1;
  }

  /** Bundle parents have no usable card image or price, so sliders skip them. */
  function isBundleHit(hit) {
    if (!hit) return false;
    if (/\bbundle\b/i.test(productTitle(hit))) return true;
    if (/\bbundle\b/i.test(productHandle(hit))) return true;
    var type = hit.product_type || hit.type || (hit._shopify && hit._shopify.type) || '';
    if (/\bbundle\b/i.test(String(type))) return true;
    var tags = tagList(hit);
    for (var i = 0; i < tags.length; i++) {
      if (tags[i] === 'bundle') return true;
    }
    var labels = hit._shopify && hit._shopify.options ? hit._shopify.options : [];
    for (var j = 0; j < labels.length; j++) {
      if (optionLooksLikeBundle(optionLabel(labels[j]))) return true;
    }
    return false;
  }

  /**
   * Shopify bundle parents use option names like "Baxx Sofa (Colour)".
   * More than one component means this is a bundle. Qty comes from the
   * first variant's Quantity option when that option exists.
   */
  function bundleComponents(product) {
    if (!product) return [];
    var labels = (product.options || []).map(optionLabel);
    var names = [];
    var seen = {};
    labels.forEach(function (label) {
      var match = String(label).match(/^(.+?)\s+\(([^)]+)\)\s*$/);
      if (!match) return;
      var kind = match[2].toLowerCase().trim();
      if (['colour', 'color', 'material', 'size', 'quantity', 'qty', 'quantities'].indexOf(kind) === -1) return;
      var name = match[1].trim();
      if (!name || seen[name]) return;
      seen[name] = true;
      names.push(name);
    });
    if (names.length < 2) return [];
    var variant = (product.variants && product.variants[0]) || {};
    var values = [variant.option1, variant.option2, variant.option3];
    return names.map(function (name) {
      var qty = 1;
      labels.forEach(function (label, index) {
        var match = String(label).match(/^(.+?)\s+\(([^)]+)\)\s*$/);
        if (!match || match[1].trim() !== name) return;
        var kind = match[2].toLowerCase().trim();
        if (kind !== 'quantity' && kind !== 'qty' && kind !== 'quantities') return;
        var parsed = parseInt(values[index], 10);
        if (parsed > 0) qty = parsed;
      });
      return { name: name, handle: handleize(name), qty: qty };
    });
  }

  function moneyFieldToCents(value, listCents) {
    var amount = Number(value);
    if (!amount || amount <= 0) return 0;
    var listDollars = Number(listCents) / 100;
    if (listDollars > 0 && amount > listDollars * 1.5) {
      var fromCents = amount / 100;
      if (fromCents > 0 && fromCents <= listDollars * 1.05) return Math.round(amount);
    }
    return Math.round(amount * 100);
  }

  function componentRoleCents(row, roleKey) {
    var product = row.product;
    var bridge = row.bridge && row.bridge.byVariantId;
    if (!product || !bridge) return 0;
    var list = productCents(product);
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var meta = bridge[String(variants[i].id)];
      if (!meta) continue;
      var cents = moneyFieldToCents(meta[roleKey], list);
      if (cents > 0) return cents;
    }
    return 0;
  }

  /**
   * Bundle parents often have no photo and a $0 variant. Use the first
   * component that has a photo, and the sum of the component prices.
   */
  function applyBundleFallback(hit, product) {
    var parts = bundleComponents(product);
    if (parts.length < 2) return Promise.resolve();
    var needsImage = shopifyImageList(product).length === 0 && !imageSrc(hit.image) && !imageSrc(hit.product_image);
    var needsPrice = productCents(product) <= 0;
    if (!needsImage && !needsPrice) return Promise.resolve();
    return Promise.all(
      parts.map(function (part) {
        return Promise.all([fetchProduct(part.handle), fetchBridge(part.handle)]).then(function (pair) {
          return { part: part, product: pair[0], bridge: pair[1] };
        });
      })
    ).then(function (rows) {
      if (needsImage) {
        for (var i = 0; i < rows.length; i++) {
          var imgs = shopifyImageList(rows[i].product);
          if (!imgs.length) continue;
          product.images = imgs;
          product.featured_image = imgs[0];
          hit.image = imgs[0];
          hit.product_image = imgs[0];
          break;
        }
      }
      if (!needsPrice) return;
      var total = 0;
      var member = 0;
      var trade = 0;
      var memberComplete = true;
      var tradeComplete = true;
      rows.forEach(function (row) {
        var qty = row.part.qty || 1;
        var cents = productCents(row.product);
        if (cents > 0) total += cents * qty;
        var memberCents = componentRoleCents(row, 'memberPricing');
        var tradeCents = componentRoleCents(row, 'tradePricing');
        if (memberCents > 0) member += memberCents * qty;
        else memberComplete = false;
        if (tradeCents > 0) trade += tradeCents * qty;
        else tradeComplete = false;
      });
      if (total <= 0) return;
      product.price = total;
      product.price_min = total;
      (product.variants || []).forEach(function (variant) {
        if (!(Number(variant.price) > 0)) variant.price = total;
      });
      hit.price = total / 100;
      hit.variants_min_price = hit.price;
      hit._bundleTotals = {
        priceCents: total,
        memberCents: memberComplete ? member : 0,
        tradeCents: tradeComplete ? trade : 0,
      };
    });
  }

  function applyBridgeBundle(hit, product, bridge) {
    if (!bridge) return;
    var image = imageSrc(bridge.bundleImage);
    var noImage = shopifyImageList(product).length === 0 && !imageSrc(hit.image) && !imageSrc(hit.product_image);
    if (image && noImage) {
      if (product) {
        product.images = [image];
        product.featured_image = image;
      }
      hit.image = image;
      hit.product_image = image;
    }
    var cents = Number(bridge.bundlePrice);
    if (!(cents > 0) || (product && productCents(product) > 0)) return;
    if (product) {
      product.price = cents;
      product.price_min = cents;
      (product.variants || []).forEach(function (variant) {
        if (!(Number(variant.price) > 0)) variant.price = cents;
      });
    }
    hit.price = cents / 100;
    hit.variants_min_price = hit.price;
    hit._bundleTotals = {
      priceCents: cents,
      memberCents: Number(bridge.bundleMember) > 0 ? Number(bridge.bundleMember) : 0,
      tradeCents: Number(bridge.bundleTrade) > 0 ? Number(bridge.bundleTrade) : 0,
    };
  }

  function applyBundleRolePrice(hit) {
    var totals = hit._bundleTotals;
    var snap = hit._snap;
    if (!totals || !snap) return;
    if (!(Number(snap.corePrice) > 0) && totals.priceCents > 0) {
      var dollars = totals.priceCents / 100;
      snap.corePrice = dollars;
      snap.fullPrice = dollars;
      snap.hasPrice = true;
      if (!(Number(snap.coreMsrp) > dollars)) snap.coreMsrp = dollars;
    }
    if (Number(snap.memberPrice) > 0) return;
    var trade = cfg.site && cfg.site.loggedInTrade;
    var roleCents = trade ? totals.tradeCents : totals.memberCents;
    if (!(roleCents > 0)) return;
    snap.memberPrice = roleCents / 100;
    snap.memberPricePrefix = trade ? 'Trade' : 'Members';
  }

  // CC 21 Oct 2026 - L17 (#64300.121): never price a card from a 'Hidden On Web' variant when a visible one exists
  // (e.g. POS-only Evergreen Outdoor Rowe). Prefer the first available visible variant for pin + price range.
  // If every variant is hidden_on_web (or Shopify returned no variants), mark the hit so PLP/search omit the card.
  // Live theme still shows those products — that is incorrect; new theme hides them.
  function repinHiddenVariant(hit, product) {
    if (!hit || !product || !Array.isArray(product.variants)) return;
    if (!product.variants.length) {
      hit._mcmAllHidden = true;
      return;
    }
    var byId = hit._plpVariantPricingById || {};
    var isHidden = function (id) {
      var row = byId[String(id)];
      return !!(row && (row.hiddenOnWeb === true || row.hiddenOnWeb === 'true' || row.hiddenOnWeb === 1));
    };
    var visible = product.variants.filter(function (v) { return !isHidden(v.id); });
    if (!visible.length) {
      hit._mcmAllHidden = true;
      return;
    }
    var prices = visible.map(function (v) { return Number(v.price) / 100; }).filter(function (n) { return n > 0; });
    if (prices.length) {
      hit.variants_min_price = Math.min.apply(null, prices);
      hit.variants_max_price = Math.max.apply(null, prices);
    }
    var vid = hit._matchedVariantId ? String(hit._matchedVariantId) : (hit.objectID != null ? String(hit.objectID) : '');
    var ok = visible.some(function (v) { return String(v.id) === vid; });
    if (ok) return;
    var pick = null;
    visible.forEach(function (v) { if (!pick && v.available) pick = v; });
    pick = pick || visible[0];
    hit._matchedVariantId = String(pick.id);
    hit._mcmRepinnedFrom = vid;
    hit.price = Number(pick.price) / 100;
    hit.compare_at_price = pick.compare_at_price ? Number(pick.compare_at_price) / 100 : 0;
    hit.option1 = pick.option1;
    hit.option2 = pick.option2;
    hit.option3 = pick.option3;
    hit.variant_title = pick.title;
    if (pick.featured_image && pick.featured_image.src) hit._matchedImage = pick.featured_image.src;
  }

  function enrichOne(hit, query) {
    if (hit._enrichedFor === query) return Promise.resolve(hit);
    pinMatchedVariant(hit);
    var handle = productHandle(hit);
    return Promise.all([fetchProduct(handle), fetchBridge(handle)]).then(function (pair) {
      var product = pair[0];
      var bridge = pair[1];
      if (bridge) {
        hit._plpVariantPricingById = bridge.byVariantId || {};
        if (bridge.pricingTag != null && bridge.pricingTag !== '') hit._plpPricingTag = bridge.pricingTag;
      }
      if (product) {
        /* Keep Algolia stock / afterpay flags before Shopify tags replace them. */
        tagList(hit).forEach(function (t) {
          var tl = t.replace(/_/g, '-');
          if (tl === 'ss-available' || tl === 'in-stock') hit._plpHadSsAvailable = true;
          if (tl === 'afterpay') hit._plpHadAfterpay = true;
        });
        hit._shopify = product;
        hit.handle = hit.handle || product.handle;
        if (product.tags) {
          hit.tags = Array.isArray(product.tags)
            ? product.tags
            : String(product.tags)
                .split(',')
                .map(function (t) {
                  return t.trim();
                })
                .filter(Boolean);
        }
      }
      repinHiddenVariant(hit, product); // CC 21 Oct 2026 - L17: skip 'Hidden On Web' variants
      applyBridgeBundle(hit, product, bridge);
      var ready = product ? applyBundleFallback(hit, product) : Promise.resolve();
      return ready.then(function () {
        applyMatchedVariantDisplay(hit, query);
        var parity = global.McmAlgoliaSnapParity;
        if (parity && typeof parity.enrichHit === 'function') {
          try {
            var ctx = {};
            Object.keys(cfg.snapCtx || {}).forEach(function (k) {
              ctx[k] = cfg.snapCtx[k];
            });
            ctx.plpPricingMode = 'matchedVariant';
            ctx.searchQuery = String(query || '');
            parity.enrichHit(hit, ctx);
          } catch (e) {
            console.warn('[MCM Algolia] enrichHit failed', handle, e);
          }
        }
        applyBundleRolePrice(hit);
        hit._enrichedFor = query;
        return hit;
      });
    });
  }

  /** Enrich hits with Shopify product data + pricing, max `concurrency` products in flight. */
  function enrich(hits, query, concurrency) {
    var list = hits || [];
    var limit = concurrency || 8;
    var cursor = 0;
    return new Promise(function (resolve) {
      if (!list.length) {
        resolve(list);
        return;
      }
      var active = 0;
      var done = 0;
      function next() {
        while (active < limit && cursor < list.length) {
          var hit = list[cursor++];
          active++;
          enrichOne(hit, query)
            .catch(function () {})
            .then(function () {
              active--;
              done++;
              if (done === list.length) resolve(list);
              else next();
            });
        }
      }
      next();
    });
  }

  /** Price the shopper sees on the card (used for price sorting). */
  function displayPrice(hit) {
    var c = hit._snap || {};
    var loggedIn = cfg.site && (cfg.site.loggedInMember || cfg.site.loggedInTrade);
    if (c.tagBased) {
      if (loggedIn && Number(c.memberPrice) > 0) return Number(c.memberPrice);
      if (Number(c.tagBased.standard) > 0) return Number(c.tagBased.standard);
      if (Number(c.fullPrice) > 0) return Number(c.fullPrice);
    }
    if (Number(c.corePrice) > 0) return Number(c.corePrice);
    if (hit._shopify && hit._shopify.price_min != null) return hit._shopify.price_min / 100;
    var p = Number(hit.variants_min_price != null ? hit.variants_min_price : hit.price);
    if (!isNaN(p) && p > 0) return p > 10000 ? p / 100 : p;
    return 0;
  }

  /* ---------- card markup ---------- */

  function priceHtml(hit) {
    var c = hit._snap;
    if (!c || !c.hasPrice) {
      var raw = displayPrice(hit);
      return raw > 0
        ? '<div class="mcm-aa-card__prices"><p class="mcm-aa-card__price">' + escapeHtml(formatMoney(raw)) + '</p></div>'
        : '';
    }
    var loggedInTrade = !!(cfg.site && cfg.site.loggedInTrade);
    var loggedIn = !!(cfg.site && (cfg.site.loggedInMember || loggedInTrade));
    var tradeAmt = c.memberPricePrefix === 'Trade' ? Number(c.memberPrice) : 0;
    var tradeFull =
      Number(c.fullPrice) ||
      Number(c.coreMsrp) ||
      Number(c.corePrice) ||
      0;

    // Logged-in Trade: retail struck; Trade black unless further sale (pricing-app or compare-at).
    // CC 27 Oct 2026 - retail line keeps "From" when list prices vary (same as logged out).
    if (loggedInTrade && tradeAmt > 0 && tradeFull > tradeAmt) {
      var tradeFurtherSale =
        !!(c.tagBased) || !!(c.hasSale && Number(c.coreMsrp) > Number(c.corePrice));
      var tradeRetailFrom =
        c.hasRetailRange != null ? !!c.hasRetailRange : !!c.hasRange;
      var tradeHtml = '<div class="mcm-aa-card__prices mcm-aa-card__prices--trade">';
      tradeHtml +=
        '<p class="mcm-aa-card__full">' +
        (tradeRetailFrom ? '<span class="mcm-aa-card__from">From </span>' : '') +
        '<s class="mcm-aa-card__compare">' +
        escapeHtml(formatMoney(tradeFull)) +
        '</s></p>';
      tradeHtml +=
        '<p class="mcm-aa-card__price' +
        (tradeFurtherSale ? ' is-sale' : '') +
        '">' +
        '<span class="mcm-aa-card__trade-label">Trade price' +
        (c.hasRange ? ' from' : '') +
        ': </span>' +
        '<span class="mcm-aa-card__current">' +
        escapeHtml(formatMoney(tradeAmt)) +
        '</span></p>';
      tradeHtml += '</div>';
      return tradeHtml;
    }

    var current;
    var compare = null;
    if (c.tagBased) {
      current = !loggedIn && Number(c.tagBased.standard) > 0 ? Number(c.tagBased.standard) : Number(c.fullPrice);
      if (Number(c.fullPrice) > current) compare = Number(c.fullPrice);
    } else {
      current = Number(c.corePrice);
      if (c.hasSale && Number(c.coreMsrp) > current) compare = Number(c.coreMsrp);
    }
    // Guest: retail + Members (both black) unless further sale → sale/member red, retail struck.
    // Logged-in member: retail struck; Members black unless further sale → Members red.
    var isMember = !!(cfg.site && cfg.site.loggedInMember);
    var memberAmt = Number(c.memberPrice) || 0;
    var memberLabel = c.memberPricePrefix === 'Trade' ? 'Trade' : 'Members';
    var memberBelow = memberLabel === 'Members' && memberAmt > 0 && memberAmt < Number(current || c.fullPrice || 0);
    if (!memberBelow && memberLabel === 'Members' && memberAmt > 0 && Number(c.fullPrice) > memberAmt) {
      memberBelow = true;
    }
    var tagDiscount = !!(c.tagBased && (Number(c.tagBased.member) > 0 || Number(c.tagBased.standard) > 0));
    var furtherSale = !!(compare || tagDiscount || c.hasSale);
    var strikeFull = isMember && memberBelow;
    var html = '<div class="mcm-aa-card__prices' + (strikeFull ? ' mcm-aa-card__prices--member' : '') + '">';
    var struckAmount = strikeFull
      ? Number(c.fullPrice) || Number(compare) || Number(current) || 0
      : Number(current) || 0;
    /* Struck retail (member) uses list price range; guests keep role/list hasRange. */
    var showFrom = strikeFull
      ? c.hasRetailRange != null
        ? !!c.hasRetailRange
        : !!c.hasRange
      : !!c.hasRange;
    var priceInner =
      (showFrom ? '<span class="mcm-aa-card__from">From </span>' : '') +
      (compare && !strikeFull ? '<s class="mcm-aa-card__compare">' + escapeHtml(formatMoney(compare)) + '</s> ' : '') +
      '<span class="mcm-aa-card__current">' +
      escapeHtml(formatMoney(strikeFull ? struckAmount : current)) +
      '</span>';
    html +=
      '<p class="mcm-aa-card__price' +
      (compare && !strikeFull ? ' is-sale' : '') +
      (strikeFull ? ' is-struck' : '') +
      '">' +
      (strikeFull ? '<s>' + priceInner + '</s>' : priceInner) +
      '</p>';
    if (memberAmt > 0 && memberLabel === 'Members') {
      var memberSale = memberBelow && furtherSale;
      html +=
        '<p class="mcm-aa-card__member' +
        (memberSale ? ' is-sale' : '') +
        '"><span class="mcm-aa-card__member-label">' +
        escapeHtml(memberLabel + ':') +
        '</span> <span class="mcm-aa-card__member-price">' +
        escapeHtml(formatMoney(c.memberPrice)) +
        '</span></p>';
    }
    html += '</div>';
    return html;
  }

  function swatchImageForValue(value) {
    var valueHandle = handleize(value);
    var names = cfg.swatchNames || [];
    var images = cfg.swatchImages || [];
    for (var i = 0; i < names.length; i++) {
      if (handleize(names[i]) === valueHandle && images[i]) {
        var src = imageSrc(Array.isArray(images[i]) ? images[i][0] : images[i]);
        if (src) return src;
      }
    }
    return '';
  }

  function optionRole(name) {
    var raw = String(name || '');
    var paren = raw.match(/\(([^)]+)\)\s*$/);
    var n = handleize(paren ? paren[1] : raw);
    if (n === 'material' || n === 'fabric' || n.indexOf('material') !== -1 || n.indexOf('fabric') !== -1) return 'material';
    if (n === 'colour' || n === 'color' || n.indexOf('colour') !== -1 || n.indexOf('color') !== -1) return 'colour';
    if (n === 'size' || n.indexOf('size') === 0) return 'size';
    return '';
  }

  function usableOptionValue(value) {
    var text = String(value == null ? '' : value).trim();
    if (!text || /^default(\s+title)?$/i.test(text)) return '';
    return text;
  }

  function singleTagValue(product, prefix) {
    var tags = product && product.tags;
    if (!tags) return '';
    if (typeof tags === 'string') tags = tags.split(',');
    var found = [];
    tags.forEach(function (tag) {
      var text = String(tag || '').trim();
      if (text.toLowerCase().indexOf(prefix.toLowerCase()) !== 0) return;
      var value = text.slice(prefix.length).trim();
      if (value) found.push(value);
    });
    return found.length === 1 ? found[0] : '';
  }

  /** Selected material or colour, then size. "Levi / 2200W". */
  function variantOptionSummary(hit) {
    var product = hit && hit._shopify;
    if (!product || !product.variants || !product.variants.length) return '';
    var variant = resolveShopifyVariant(hit, product) || product.variants[0];
    var material = '';
    var colour = '';
    var size = '';
    (product.options || []).forEach(function (opt, index) {
      var name = typeof opt === 'string' ? opt : (opt && opt.name) || '';
      var position = opt && typeof opt === 'object' ? Number(opt.position) : index + 1;
      var slot = position >= 1 ? position - 1 : index;
      var values = variant.options || [variant.option1, variant.option2, variant.option3];
      var value = usableOptionValue(values[slot]);
      if (!value) return;
      var role = optionRole(name);
      if (role === 'material' && !material) material = value;
      else if (role === 'colour' && !colour) colour = value;
      else if (role === 'size' && !size) size = value;
    });
    var finish = material || colour || singleTagValue(product, 'Material:') || singleTagValue(product, 'Colour:');
    var parts = [];
    if (finish) parts.push(finish);
    if (size) parts.push(size);
    if (parts.length) return parts.join(' / ');
    return usableOptionValue(variant.public_title || variant.title);
  }

  function swatchesHtml(hit) {
    var product = hit._shopify;
    if (!product) return '';
    var limit = 4;
    var items = [];
    var total = 0;

    var siblings = [];
    (product.tags || []).forEach(function (t) {
      var parts = String(t).split(':');
      if (parts[0] === 'sibling' && parts[1]) siblings.push(parts[1]);
    });

    if (siblings.length > 1) {
      total = siblings.length;
      siblings.slice(0, limit).forEach(function (handle) {
        items.push(
          '<a class="mcm-aa-swatch is-loading" href="/products/' +
            escapeHtml(handle) +
            '" data-mcm-aa-sibling="' +
            escapeHtml(handle) +
            '" aria-label="' +
            escapeHtml(handle.replace(/-/g, ' ')) +
            '"></a>'
        );
      });
    } else {
      var colourOption = null;
      var colourIdx = -1;
      var sizeOption = null;
      (product.options || []).forEach(function (opt, index) {
        var n = handleize(opt && opt.name);
        if (!colourOption && (n === 'color' || n === 'colour' || n === 'material')) {
          colourOption = opt;
          colourIdx = opt && typeof opt === 'object' && opt.position ? Number(opt.position) - 1 : index;
        } else if (!sizeOption && n === 'size') {
          sizeOption = opt;
        }
      });
      // CC 21 Oct 2026 - L16 (#64300.120): same rules as the old theme (object.product-thumbnail-swatches):
      // more than 1 colour -> swatches; else more than 1 size -> '+ more sizes'; else no size option and 1 colour -> that 1 swatch
      var colourCount = colourOption && colourOption.values ? colourOption.values.length : 0;
      var sizeCount = sizeOption && sizeOption.values ? sizeOption.values.length : 0;
      if (colourCount < 2) {
        if (sizeCount > 1) return '<div class="mcm-aa-card__swatches"><span class="mcm-aa-swatch-more mcm-aa-swatch-more--sizes">+ more sizes</span></div>';
        if (!(sizeCount === 0 && colourCount === 1)) return '';
      }
      total = colourCount;
      // CC 21 Oct 2026 - L15 (#64300.119): colour swatches link to the PDP with that colour selected
      // (first in-stock variant with that colour, otherwise the first variant with it)
      var swatchBase = String(productUrl(hit)).split('?')[0];
      var swatchHref = function (val) {
        var hitAny = null, hitAvail = null;
        (product.variants || []).forEach(function (v) {
          var vals = v.options || [v.option1, v.option2, v.option3];
          if (String(vals[colourIdx]) !== String(val)) return;
          if (!hitAny) hitAny = v;
          if (!hitAvail && v.available !== false) hitAvail = v;
        });
        var pick = hitAvail || hitAny;
        return pick && pick.id ? swatchBase + '?variant=' + pick.id : swatchBase;
      };
      colourOption.values.slice(0, limit).forEach(function (val) {
        items.push(
          '<a class="mcm-aa-swatch" href="' +
            escapeHtml(swatchHref(val)) +
            '" title="' +
            escapeHtml(val) +
            '" aria-label="' +
            escapeHtml(val) +
            '"><img src="' +
            escapeHtml(swatchImageForValue(val)) +
            '" alt="' +
            escapeHtml(val) +
            '" width="10" height="10" loading="lazy" onerror="this.parentNode.classList.add(\'is-missing\');this.remove()"></a>'
        );
      });
    }
    if (total > limit) items.push('<span class="mcm-aa-swatch-more">+' + (total - limit) + '</span>');
    return '<div class="mcm-aa-card__swatches">' + items.join('') + '</div>';
  }

  function hydrateSwatches(rootEl) {
    if (!rootEl) return;
    Array.prototype.forEach.call(rootEl.querySelectorAll('[data-mcm-aa-sibling].is-loading'), function (el) {
      var handle = el.getAttribute('data-mcm-aa-sibling');
      fetchProduct(handle).then(function (p) {
        el.classList.remove('is-loading');
        var src = p && (p.featured_image || (p.images && imageSrc(p.images[0])));
        if (!src) {
          el.classList.add('is-missing');
          return;
        }
        el.innerHTML = '<img src="' + escapeHtml(sizedImage(src, 40)) + '" alt="" width="10" height="10" loading="lazy">';
      });
    });
  }

  function isPlainBackground(bg) {
    var v = String(bg || '').trim().toLowerCase();
    return !v || v === '#fff' || v === '#ffffff' || v === 'white' || v === 'transparent' || v === 'rgba(0,0,0,0)';
  }

  function badgeHtml(badge, position) {
    if (!badge || !badge.label) return '';
    var color = badge.color || '#181818';
    var transparent = badge.transparent === true;
    var bg = badge.bgColor || '#ffffff';
    var style = 'color:' + escapeHtml(color) + ';';
    if (!transparent) style += 'background:' + escapeHtml(bg) + ';';
    return (
      '<span class="mcm-aa-card__badge mcm-aa-card__badge--' +
      position +
      (transparent ? ' is-clear' : ' has-bg') +
      '" style="' +
      style +
      '">' +
      escapeHtml(badge.label) +
      '</span>'
    );
  }

  function badgesHtml(hit) {
    var s = hit._snap;
    if (!s) return '';
    var html = '';
    if (s.overlayBadge) html += badgeHtml(s.overlayBadge, 'top-left');
    if (s.overlayBadgeRight) html += badgeHtml(s.overlayBadgeRight, 'top-right');
    if (s.bottomBadgeLeft) html += badgeHtml(s.bottomBadgeLeft, 'bottom-left');
    if (s.bottomBadgeRight) html += badgeHtml(s.bottomBadgeRight, 'bottom-right');
    return html;
  }

  /**
   * opts.pending — Algolia data only (prices still loading)
   * opts.imageWidth — CDN width for the card image
   */
  function cardHtml(hit, opts) {
    opts = opts || {};
    if (hit && hit._mcmAllHidden && !opts.pending) return '';
    var url = productUrl(hit);
    var title = productTitle(hit);
    var imgs = productImages(hit, opts);
    var width = opts.imageWidth || 800;
    var objectId = hit.objectID != null ? String(hit.objectID).split('_')[0] : '';
    var variantSummary = opts.variantSummary && !opts.pending ? variantOptionSummary(hit) : '';
    return (
      '<div class="mcm-aa-card' +
      (opts.pending ? ' is-pending' : '') +
      '" data-aa-object-id="' +
      escapeHtml(objectId) +
      '"' +
      (hit.__aaQueryID ? ' data-aa-query-id="' + escapeHtml(hit.__aaQueryID) + '"' : '') +
      (hit.__aaPosition ? ' data-aa-position="' + String(hit.__aaPosition) + '"' : '') +
      '>' +
      '<a class="mcm-aa-card__media' +
      (imgs.hover ? ' has-hover' : '') +
      '" href="' +
      escapeHtml(url) +
      '" tabindex="-1" aria-hidden="true">' +
      (imgs.primary
        ? '<img class="mcm-aa-card__img" src="' +
          escapeHtml(sizedImage(imgs.primary, width)) +
          '" alt="" loading="lazy" width="800" height="800">'
        : '') +
      (imgs.hover
        ? '<img class="mcm-aa-card__img mcm-aa-card__img--hover" src="' +
          escapeHtml(sizedImage(imgs.hover, width)) +
          '" alt="" loading="lazy" width="800" height="800">'
        : '') +
      (opts.pending ? '' : badgesHtml(hit)) +
      '</a>' +
      '<div class="mcm-aa-card__info">' +
      '<div class="mcm-aa-card__row">' +
      '<a class="mcm-aa-card__title" href="' +
      escapeHtml(url) +
      '">' +
      escapeHtml(title) +
      '</a>' +
      (opts.pending ? '' : swatchesHtml(hit)) +
      '</div>' +
      (variantSummary ? '<p class="mcm-aa-card__variant">' + escapeHtml(variantSummary) + '</p>' : '') +
      (opts.pending ? '<div class="mcm-aa-card__prices mcm-aa-card__prices--pending" aria-hidden="true"><span></span><span></span></div>' : priceHtml(hit)) +
      '</div>' +
      '</div>'
    );
  }

  /**
   * Products in a collection, in Algolia rank order.
   * _collections:<title> is the category page identifier. Handle and numeric id are fallbacks.
   * Collection-only products stay visible; ss_hide / member-hidden products do not.
   * opts: { limit, collectionId, title }
   */
  function browseCollection(handle, opts) {
    opts = opts || {};
    var limit = opts.limit || 12;
    var collectionId = opts.collectionId ? String(opts.collectionId) : '';
    var title = opts.title ? String(opts.title) : '';
    var cacheKey = ['collection', handle || '', collectionId, title, limit].join('|');
    if (searchCache[cacheKey]) return searchCache[cacheKey];

    function quote(value) {
      return '"' + String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
    }

    function visible(hit) {
      if (!hit || !productHandle(hit)) return false;
      var tags = tagList(hit);
      if (tags.indexOf('ss_hide') !== -1) return false;
      if (cfg.site && cfg.site.loggedInMember && tags.indexOf('ss_loyalty') !== -1) return false;
      return true;
    }

    function dedupe(hits) {
      var best = {};
      var order = [];
      (hits || []).forEach(function (hit) {
        if (!visible(hit)) return;
        /* First hit per product keeps Algolia pin / conditionless order. */
        var key =
          hit.product_id != null
            ? 'pid:' + String(hit.product_id)
            : hit.id != null
              ? 'id:' + String(hit.id)
              : productHandle(hit)
                ? 'h:' + productHandle(hit)
                : 'o:' + String(hit.objectID || '');
        if (best[key]) return;
        best[key] = hit;
        order.push(key);
      });
      return order.map(function (key) {
        return best[key];
      });
    }

    function facetToken(attr, value) {
      var text = String(value || '');
      if (/[\s:"\\]/.test(text)) text = quote(text);
      return attr + ':' + text;
    }

    function titleCaseHandle(handle) {
      return String(handle || '')
        .split('-')
        .filter(Boolean)
        .map(function (word) {
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
    }

    var strategies = [];
    if (title) {
      strategies.push({ facetFilters: [[facetToken('_collections', title)]] });
      strategies.push({ filters: facetToken('_collections', title) });
    }
    var categoryLabel = titleCaseHandle(handle);
    if (categoryLabel && categoryLabel !== title) {
      strategies.push({ facetFilters: [[facetToken('_collections', categoryLabel)]] });
      strategies.push({ filters: facetToken('_collections', categoryLabel) });
    }
    if (handle) {
      strategies.push({ facetFilters: [['collections:' + handle]] });
      strategies.push({ filters: 'collections:' + quote(handle) });
    }
    if (title && title !== handle) {
      strategies.push({ facetFilters: [['collections:' + title]] });
      strategies.push({ filters: 'collections:' + quote(title) });
    }
    if (collectionId) {
      strategies.push({ facetFilters: [['collection_ids:' + collectionId]] });
      strategies.push({ filters: 'collection_ids:' + collectionId });
    }

    var promise = new Promise(function (resolve, reject) {
      ready(function (c) {
        if (!strategies.length) {
          resolve([]);
          return;
        }
        var index = c.initIndex(cfg.indexProducts);
        function attempt(i) {
          if (i >= strategies.length) {
            resolve([]);
            return;
          }
          var params = {
            hitsPerPage: 1000,
            page: 0,
            clickAnalytics: true,
            enableRules: true,
            attributesToRetrieve: RETRIEVE,
          };
          if (handle) {
            params.ruleContexts = [handle];
            params.analyticsTags = ['category-page'];
          }
          var strategy = strategies[i];
          if (strategy.facetFilters) params.facetFilters = strategy.facetFilters;
          if (strategy.filters) params.filters = strategy.filters;
          index
            .search('', params)
            .then(function (res) {
              var batch = (res && res.hits) || [];
              var queryID = res && res.queryID;
              batch.forEach(function (hit, n) {
                if (!hit) return;
                if (queryID) hit.__aaQueryID = queryID;
                hit.__aaPosition = n + 1;
              });
              /* Keep Algolia merchandising order (conditionless pins / category rules). */
              var hits = dedupe(batch);
              if (hits.length) resolve(hits.slice(0, limit));
              else attempt(i + 1);
            })
            .catch(function (err) {
              if (i === strategies.length - 1) {
                console.warn('[MCM Algolia] collection browse failed', handle, err);
                reject(err);
                return;
              }
              attempt(i + 1);
            });
        }
        attempt(0);
      });
    });
    searchCache[cacheKey] = promise;
    promise.catch(function () {
      delete searchCache[cacheKey];
    });
    return promise;
  }

  /**
   * Cart "Recommended for you": complementary types for what is already in the cart.
   * Colour only changes order. A merchandised upsell handle leads. No bestseller fill.
   * items: [{ handle, title, product_type, colours, upsell_handle }]
   */
  function complementTerms(item) {
    var blob = String((item && item.product_type) || '') + ' ' + String((item && item.title) || '');
    blob = blob.toLowerCase();
    if (/membership|gift card|gift-card/.test(blob)) return [];
    var terms;
    if (/ottoman/.test(blob)) terms = ['armchair', 'cushion', 'coffee table', 'side table'];
    else if (/armchair/.test(blob)) terms = ['sofa', 'ottoman', 'cushion', 'coffee table', 'side table'];
    else if (/sofa|settee|loveseat|modular/.test(blob)) terms = ['armchair', 'cushion', 'coffee table', 'side table'];
    else if (/bedside/.test(blob)) terms = ['table lamp', 'cushion', 'rug'];
    else if (/bedhead/.test(blob)) terms = ['bedside table', 'cushion', 'rug'];
    else if (/\bbeds?\b/.test(blob)) terms = ['bedside table', 'bedhead', 'cushion', 'rug'];
    else if (/coffee table/.test(blob)) terms = ['armchair', 'sofa', 'cushion', 'side table'];
    else if (/side table|console/.test(blob)) terms = ['table lamp', 'cushion', 'sofa'];
    else if (/dining table|\bdesk\b/.test(blob)) terms = ['dining chair', 'stool', 'sideboard', 'buffet'];
    else if (/\bchairs?\b/.test(blob)) terms = ['dining table', 'cushion'];
    else if (/\btables?\b/.test(blob)) terms = ['chair', 'stool', 'side table', 'buffet'];
    else if (/stool|bench/.test(blob)) terms = ['dining table', 'cushion'];
    else terms = ['cushion', 'side table', 'lamp', 'rug'];
    return terms.filter(function (term) {
      return blob.indexOf(term) === -1;
    });
  }

  function cartComplementHits(items, opts) {
    opts = opts || {};
    var limit = opts.limit || 8;
    var taken = {};
    var colours = [];
    var queries = [];
    var seenQuery = {};
    var upsells = [];
    (items || []).forEach(function (item) {
      if (!item) return;
      var handle = String(item.handle || '').toLowerCase();
      if (handle) taken[handle] = true;
      (item.colours || []).forEach(function (colour) {
        var value = String(colour || '').trim();
        if (!value) return;
        if (colours.some(function (existing) { return existing.toLowerCase() === value.toLowerCase(); })) return;
        colours.push(value);
      });
      var upsell = String(item.upsell_handle || '').trim();
      if (upsell && !taken[upsell.toLowerCase()] && upsells.indexOf(upsell) === -1) upsells.push(upsell);
      complementTerms(item).forEach(function (query) {
        var key = query.toLowerCase();
        if (seenQuery[key]) return;
        seenQuery[key] = true;
        queries.push(query);
      });
    });

    function score(hit) {
      var value = 0;
      var handle = productHandle(hit).toLowerCase();
      if (hit._upsell || upsells.some(function (upsell) { return upsell.toLowerCase() === handle; })) value += 100;
      var title = productTitle(hit).toLowerCase();
      colours.forEach(function (colour) {
        var want = colour.toLowerCase();
        if (hitHasColour(hit, colour) || title.indexOf(want) !== -1) value += 40;
      });
      return value;
    }

    function acceptable(hit) {
      if (!hit || isBundleHit(hit)) return false;
      var handle = productHandle(hit).toLowerCase();
      if (!handle || taken[handle]) return false;
      if (handle === 'loyalty-membership' || handle.indexOf('gift-card') !== -1) return false;
      var type = String(hit.product_type || hit.type || '').toLowerCase();
      if (type === 'art' || type.indexOf('artwork') !== -1 || type.indexOf('art print') !== -1) return false;
      var tags = tagList(hit);
      if (tags.indexOf('ss_hide') !== -1) return false;
      if (cfg.site && cfg.site.loggedInMember && tags.indexOf('ss_loyalty') !== -1) return false;
      return true;
    }

    return new Promise(function (resolve) {
      ready(function (c) {
        if (!queries.length && !upsells.length) {
          resolve([]);
          return;
        }
        var index = c.initIndex(cfg.indexProducts);
        var jobs = queries.slice(0, 5).map(function (query) {
          return index
            .search(query, {
              hitsPerPage: 12,
              queryType: 'prefixNone',
              ignorePlurals: true,
              removeWordsIfNoResults: 'none',
              attributesToRetrieve: RETRIEVE,
            })
            .then(function (res) {
              return (res && res.hits) || [];
            })
            .catch(function () {
              return [];
            });
        });
        upsells.forEach(function (handle) {
          jobs.push(
            index
              .search('', {
                filters: 'handle:"' + handle.replace(/"/g, '') + '"',
                hitsPerPage: 1,
                attributesToRetrieve: RETRIEVE,
              })
              .then(function (res) {
                return ((res && res.hits) || []).map(function (hit) {
                  hit._upsell = true;
                  if (!hit.handle) hit.handle = handle;
                  return hit;
                });
              })
              .catch(function () {
                return [];
              })
          );
        });
        Promise.all(jobs)
          .then(function (groups) {
            var seen = {};
            var hits = [];
            groups.forEach(function (list) {
              list.forEach(function (hit) {
                if (!acceptable(hit)) return;
                var key = productHandle(hit).toLowerCase();
                if (seen[key]) return;
                seen[key] = true;
                hits.push(hit);
              });
            });
            hits.sort(function (a, b) {
              return score(b) - score(a);
            });
            resolve(hits.slice(0, limit));
          })
          .catch(function () {
            resolve([]);
          });
      });
    });
  }

  function popularQueries() {
    return String(cfg.popularQueries || '')
      .split(/\r?\n|\\n/)
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  global.McmAlgolia = {
    cfg: cfg,
    ready: ready,
    escapeHtml: escapeHtml,
    formatMoney: formatMoney,
    handleize: handleize,
    productTitle: productTitle,
    productHandle: productHandle,
    isBundleHit: isBundleHit,
    categoriesOf: categoriesOf,
    collectionUrl: collectionUrl,
    searchProducts: searchProducts,
    browseCollection: browseCollection,
    cartComplementHits: cartComplementHits,
    searchCollections: searchCollections,
    searchJournals: searchJournals,
    journalCardsHtml: journalCardsHtml,
    fetchSuggestions: fetchSuggestions,
    enrich: enrich,
    displayPrice: displayPrice,
    cardHtml: cardHtml,
    hydrateSwatches: hydrateSwatches,
    popularQueries: popularQueries,
    tagList: tagList,
  };
})(window);
